
import { motion } from 'framer-motion';
import { Trophy, Sparkles, PartyPopper, Award, Star, Flame } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';

interface WorkoutCompleteProps {
  onClose: () => void;
  streakCount?: number;
}

export const WorkoutComplete = ({ onClose, streakCount = 1 }: WorkoutCompleteProps) => {
  const [confettiTriggered, setConfettiTriggered] = useState(false);
  
  useEffect(() => {
    if (!confettiTriggered) {
      // Trigger confetti when component mounts
      triggerConfetti();
      setConfettiTriggered(true);
    }
  }, [confettiTriggered]);

  const triggerConfetti = () => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 5000
    };

    function fire(particleRatio: number, opts: any) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio)
      });
    }

    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    });

    fire(0.2, {
      spread: 60,
    });

    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8
    });

    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2
    });

    fire(0.1, {
      spread: 120,
      startVelocity: 45,
    });
  };

  // Animation variants for confetti-like elements
  const itemVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: (i: number) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        type: "spring",
        stiffness: 200,
        damping: 10
      }
    })
  };

  // Trophy bounce animation
  const trophyVariants = {
    initial: { scale: 0, rotate: -180 },
    animate: { 
      scale: 1, 
      rotate: 0,
      transition: { 
        type: "spring", 
        delay: 0.2, 
        duration: 0.8, 
        bounce: 0.6 
      }
    }
  };
  
  // Get appropriate message based on streak
  const getStreakMessage = () => {
    if (streakCount >= 10) return "Phenomenal dedication! You're unstoppable!";
    if (streakCount >= 7) return "Amazing streak! You're becoming a master!";
    if (streakCount >= 3) return "Fantastic work! You're building great momentum!";
    if (streakCount > 1) return "You're on a roll! Keep it up!";
    return "Great start! Come back tomorrow to build your streak!";
  };
  
  return (
    <motion.div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="w-full max-w-md px-4"
      >
        <Card className="glass-card overflow-hidden relative p-6 border-2 border-primary/30">
          {/* Floating animated elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(15)].map((_, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                className="absolute"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  transform: `rotate(${Math.random() * 360}deg)`,
                }}
              >
                {i % 5 === 0 ? (
                  <Sparkles className="h-5 w-5 text-yellow-400" />
                ) : i % 5 === 1 ? (
                  <PartyPopper className="h-6 w-6 text-purple-500" />
                ) : i % 5 === 2 ? (
                  <Star className="h-4 w-4 text-amber-300" />
                ) : i % 5 === 3 ? (
                  <Award className="h-5 w-5 text-green-400" />
                ) : (
                  <div className="h-3 w-3 rounded-full bg-primary/80" />
                )}
              </motion.div>
            ))}
          </div>
          
          <div className="text-center relative z-10">
            <motion.div 
              className="flex justify-center mb-4"
              variants={trophyVariants}
              initial="initial"
              animate="animate"
            >
              <div className="relative">
                <Trophy className="h-24 w-24 text-amber-400" />
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1, duration: 0.5 }}
                  className="absolute -top-3 -right-3"
                >
                  <Sparkles className="h-8 w-8 text-yellow-300" />
                </motion.div>
              </div>
            </motion.div>
            
            <motion.h2 
              className="text-3xl font-bold mb-2 font-lexend bg-gradient-to-r from-primary to-amber-400 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Workout Complete!
            </motion.h2>
            
            <motion.p
              className="text-muted-foreground mb-5 text-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Congratulations on completing today's AI training workout!
            </motion.p>
            
            <motion.div
              className="bg-primary/10 rounded-lg p-4 mb-6 border border-primary/20"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Flame className="text-primary h-5 w-5" />
                <p className="font-medium text-lg">
                  Current streak: <span className="text-primary font-bold">{streakCount} day{streakCount !== 1 ? 's' : ''}</span>
                </p>
              </div>
              <p className="text-muted-foreground">
                {getStreakMessage()}
              </p>
            </motion.div>
            
            <div className="flex flex-col gap-3">
              <Button 
                onClick={onClose}
                size="lg"
                className="font-medium"
              >
                Continue Training
              </Button>
              <Button variant="outline" asChild>
                <Link to="/dashboard">
                  Go to Dashboard
                </Link>
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
};
